/**
 * Simple API Performance Monitor
 * 
 * A lightweight utility to track API performance without complex dependencies
 */
import { performanceMonitor } from './performanceMonitor';

// Thresholds for API warnings (in milliseconds)
const WARNING_THRESHOLD = 1000;  // 1 second
const CRITICAL_THRESHOLD = 5000; // 5 seconds
const EXTREME_THRESHOLD = 30000; // 30 seconds

// Important endpoints to watch
const IMPORTANT_ENDPOINTS = [
  '/api/companies/fetchcompanies',
  '/api/jobs/fetchjobs'
];

// Simple API performance monitor
const simpleApiMonitor = {
  // Store last checked time to avoid duplicate alerts
  lastCheckedTime: 0,
  
  // Initialize the monitor
  initialize: function() {
    console.log('🔍 Simple API Performance Monitoring initialized');
    
    // Check metrics every 10 seconds
    this.intervalId = setInterval(() => {
      this.checkPerformance();
    }, 10000);
    
    // Return cleanup function
    return () => {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    };
  },
  
  // Check API performance
  checkPerformance: function() {
    try {
      // Skip if performanceMonitor is not available
      if (!performanceMonitor || typeof performanceMonitor.getMetrics !== 'function') {
        return;
      }
      
      const metrics = performanceMonitor.getMetrics();
      
      // Skip if no API metrics
      if (!metrics || !metrics.api) {
        return;
      }
      
      const apiMetrics = metrics.api;
      
      // Use for...in loop instead of forEach
      for (const name in apiMetrics) {
        if (Object.prototype.hasOwnProperty.call(apiMetrics, name)) {
          const metric = apiMetrics[name];
          
          // Skip invalid metrics
          if (!metric || typeof metric !== 'object' || typeof metric.duration !== 'number') {
            continue;
          }
          
          // Extract endpoint name
          let endpoint = name;
          if (name.includes(' ')) {
            endpoint = name.split(' ')[1];
          }
          
          // Check duration against thresholds
          const duration = metric.duration;
          
          if (duration > EXTREME_THRESHOLD) {
            console.error(`⚠️ EXTREME PERFORMANCE ISSUE: ${endpoint} took ${(duration/1000).toFixed(2)}s`);
            this.suggestOptimizations(endpoint, duration);
          } 
          else if (duration > CRITICAL_THRESHOLD) {
            console.error(`🔴 Critical: ${endpoint} took ${(duration/1000).toFixed(2)}s`);
          }
          else if (duration > WARNING_THRESHOLD) {
            console.warn(`🟠 Slow: ${endpoint} took ${(duration/1000).toFixed(2)}s`);
          }
          else if (IMPORTANT_ENDPOINTS.includes(endpoint)) {
            console.log(`🟢 ${endpoint} performed well: ${(duration/1000).toFixed(2)}s`);
          }
        }
      }
    } catch (err) {
      // Silent fail
    }
  },
  
  // Log optimization suggestions
  suggestOptimizations: function(endpoint, duration) {
    console.group('🔧 Performance Optimization Suggestions');
    console.log(`Endpoint: ${endpoint}`);
    console.log(`Duration: ${(duration/1000).toFixed(2)}s`);
    
    if (endpoint.includes('/api/companies/fetchcompanies')) {
      console.log('1. Use companyDataManager for fetching companies data');
      console.log('2. Consider implementing pagination on the server');
      console.log('3. Review database indexes for this query');
    } 
    else {
      console.log('1. Consider implementing caching');
      console.log('2. Review database query performance');
      console.log('3. Check for N+1 query problems');
    }
    
    console.groupEnd();
  },
  
  // Generate a simple report
  generateReport: function() {
    try {
      console.group('📊 API Performance Summary');
      
      if (!performanceMonitor || !performanceMonitor.getMetrics) {
        console.log('No performance data available');
        console.groupEnd();
        return;
      }
      
      const metrics = performanceMonitor.getMetrics();
      
      if (!metrics || !metrics.api) {
        console.log('No API metrics recorded');
        console.groupEnd();
        return;
      }
      
      const apiMetrics = metrics.api;
      const report = [];
      
      for (const name in apiMetrics) {
        if (Object.prototype.hasOwnProperty.call(apiMetrics, name)) {
          const metric = apiMetrics[name];
          
          if (metric && typeof metric === 'object' && typeof metric.duration === 'number') {
            let endpoint = name;
            if (name.includes(' ')) {
              endpoint = name.split(' ')[1];
            }
            
            report.push({
              endpoint: endpoint,
              duration: metric.duration,
              calls: metric.count || 1
            });
          }
        }
      }
      
      if (report.length === 0) {
        console.log('No valid API metrics to report');
      } else {
        // Sort by duration (descending)
        report.sort(function(a, b) {
          return b.duration - a.duration;
        });
        
        // Convert to a simple table format
        const tableData = report.map(function(item) {
          return {
            endpoint: item.endpoint,
            duration: (item.duration / 1000).toFixed(2) + 's',
            calls: item.calls
          };
        });
        
        console.table(tableData);
      }
      
      console.groupEnd();
    } catch (err) {
      console.error('Error generating report:', err);
    }
  }
};

export default simpleApiMonitor;